import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * The Account page's sub-navigation, shared by two renderers: the horizontal
 * tab bar on the page itself (md and up) and the mobile hamburger panel in
 * App.vue (below md). Both must agree on which tabs a tenant is allowed to see,
 * so the gating lives here rather than being duplicated per surface.
 */
export type TabId =
  | 'billing'
  | 'users'
  | 'materials'
  | 'qr'
  | 'engravers'
  | 'downloads'
  | 'troubleshooting'
  | 'shipping'

export const DEFAULT_TAB: TabId = 'billing'

export function useAccountTabs() {
  const authStore = useAuthStore()

  const tabs = computed<{ id: TabId; label: string }[]>(() => {
    // Fulfillment ("no hardware") tenants have no engraver/downloads/materials
    // surface — just billing, users, and a shipping address.
    if (authStore.isFulfillment) {
      return [
        { id: 'billing', label: 'Billing' },
        { id: 'users', label: 'Users' },
        { id: 'shipping', label: 'Shipping address' },
      ]
    }
    const list: { id: TabId; label: string }[] = [
      { id: 'billing', label: 'Billing' },
      { id: 'users', label: 'Users' },
      { id: 'materials', label: 'Materials' },
    ]
    // Beta-gated: only tag-designer tenants ever see the QR Code tab.
    if (authStore.tagDesignerEnabled) list.push({ id: 'qr', label: 'QR Code' })
    list.push({ id: 'engravers', label: 'Engravers' })
    list.push({ id: 'downloads', label: 'Downloads' })
    list.push({ id: 'troubleshooting', label: 'Troubleshooting' })
    return list
  })

  // A `?tab=` value is untrusted: it can name a tab this tenant isn't entitled
  // to (`?tab=qr` without the beta flag), or one that doesn't exist. Those
  // panels are independently `v-if`-gated and would render nothing, leaving a
  // blank page — so anything not in the allowed list falls back to Billing.
  function resolveTab(raw: unknown): TabId {
    return tabs.value.some(t => t.id === raw) ? (raw as TabId) : DEFAULT_TAB
  }

  return { tabs, resolveTab }
}
